<template>
  <b-container fluid>
    <b-row>
      <b-col md="6" lg="5">
        <h1>Recent Orders</h1>
      </b-col>
      <b-col md="6" lg="7" class="pb-md-0 pb-5" v-if="showSearchForm()">
        <SearchForm @input="onSearchTermChange" :search-text-default="search"/>
      </b-col>
    </b-row>
    <b-row>
      <AdminOrderFilter v-if="isStaffDashboard" @input="onStatusChange" :status-initial="filterInitialStatus"/>
      <OrderFilter v-else :in="recentOrders" @out="onStatusChange" :is-busy="recentOrdersBusy"/>
    </b-row>

    <ItemsOnPageSelect
      v-if="isStaffDashboard"
      :items-on-page-default="itemsOnPage"
      :on-select-change="onItemsOnPageChange"
      :options="itemsOnPageOptions"
    />

    <OrderDetailCommon v-for="order in orderListToShow"
                       :order="order"
                       :id="order.id"
                       :is-back-button-shown="false"
                       :key="order.id"
    >
      <div slot="shipping" slot-scope="{order}"><slot name="shipping" :order="order"/></div>
      <div slot="actions" slot-scope="{order}"><slot name="actions" :order="order"/></div>
      <div slot="line-actions" slot-scope="{order, line}"><slot name="line-actions" :order="order" :line="line"/></div>
    </OrderDetailCommon>

    <template v-if="isStaffDashboard">
      <article v-if="!this.recentOrders" class="order no-orders">
        <WaveSpinner v-if="recentOrdersBusy"/>
        <span v-else>
          <span v-if="statusSelected">No orders with status "{{ statusSelected }}" found</span>
          <span v-else>No orders found</span>
        </span>
      </article>
      <Pagination
        :current-page="pageNumberOrders || 1"
        :total-items="totalOrders || 0"
        :items-on-page="itemsOnPage"
        :on-page-number-change="onPageNumberChange"
      />
    </template>
    <template v-else>
      <article v-if="!orderList.length" class="order no-orders">
        <WaveSpinner v-if="recentOrdersBusy"/>
        <span v-else-if="orderListFiltered.length">No orders found</span>
      </article>
    </template>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { READY_FOR_PRODUCTION } from "@/lib/orderStatus";
import { listWithSearchAndPaginationMixin } from '@/services/mixins';
import OrderDetailCommon from "./OrderDetailCommon";
import OrderFilter from './OrderFilter';
import AdminOrderFilter from './AdminOrderFilter';
import SearchForm from '@/components/shared/SearchForm';
import debounce from 'lodash/debounce';

export default {
  components: {
    OrderDetailCommon,
    OrderFilter,
    AdminOrderFilter,
    SearchForm,
  },
  mixins: [listWithSearchAndPaginationMixin],
  data() {
    return {
      orderList: [],
      orderListFiltered: [],
      statusSelected: this.query.statusSelected || READY_FOR_PRODUCTION,
      filterInitialStatus: this.query.statusSelected || READY_FOR_PRODUCTION,

      queryStringKeysToCheck: ['search', 'pageNumber', 'itemsOnPage', 'statusSelected'],

      useMountedFromMixin: false,
      functionToCallOnChange: this.loadOrdersPaginated,
      listPathName: 'order-list',
    }
  },
  computed: {
    ...mapGetters(['flags', 'user', 'recentOrders', 'recentOrdersBusy', 'pageNumberOrders', 'totalOrders']),
    isStaffDashboard() {
      return this.$route.path.startsWith('/admin');
    },
    orderListToShow() {
      if (this.isStaffDashboard) {
        return this.recentOrders || [];
      }
      return this.orderList;
    },
    loadingParams() {
      return {
        itemsOnPage: this.itemsOnPage,
        pageNumber: this.pageNumber,
        status: this.statusSelected,
        search: this.search,
        allteams: 1,
      }
    },
  },
  methods: {
    ...mapActions(['loadRecentOrders', 'updateRecentOrders', 'loadOrdersPaginated', 'reOrder']),
    showSearchForm() {
      // Admins can search (not orderers)
      return this.isStaffDashboard || this.user.user_type === 'admin';
    },
    onSearchTermChange: debounce(function (search) {
      if (this.isStaffDashboard) {
        // Avoid searching on short search terms (1 to 3 chars)
        if (search.length && search.length < 3) return;

        // If `search == ''` (empty string) it is better to set it to `null`.
        // In this case in URL's query string we will not have "search=".
        const newSearch = search.trim() || null;
        if (this.search !== newSearch) {
          this.search = newSearch;
          // Start from the 1st page on each search term change.
          this.pageNumber = 1;
          this.loadList();
        }
      } else {
        // Search should be made against already filtered results.
        if (search) {
          this.search = search.toLowerCase();
          this.orderList = this.orderListFiltered.filter(o => {
            return o.number.includes(this.search) || o.guest_email.toLowerCase().includes(this.search)
          })
        } else {
          this.orderList = this.orderListFiltered;
        }
      }
    }, 250),
    onStatusChange(data) {
      if (this.isStaffDashboard) {
        if (this.statusSelected !== data) {
          this.statusSelected = data;
          // Start from the 1st page on each status change.
          this.pageNumber = 1;
          this.loadList();
        }
      } else {
        this.orderListFiltered = data;
        // Take into account the search term if it is entered.
        this.onSearchTermChange(this.search);
      }
    },
  },
  mounted() {
    if (this.isStaffDashboard) {
      this.loadList();
    } else {
      this.loadRecentOrders();
    }
  }
}
</script>

<style scoped>
  h1 {
    font-size: 2.0rem;
    margin-top: 0;
    margin-left: -17px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/dashboard/orders/OrdersList.vue